/**
 * Estilos do Relatório. Mobile first: os valores-base são os do celular e os
 * `min-width` abrem o layout em tablet e desktop.
 *
 * Limpo antes de denso: uma lista com divisórias (não um mural de cartões),
 * texto neutro e UM sinal de cor por linha, o ponto ao lado da situação.
 * Fase e etapa são texto e cinza, nunca cor.
 */
import styled, { css, keyframes } from "styled-components";

import { PageContainer } from "../../styles/shared";
import { BP_MOBILE, BP_TABLET } from "../../styles/canonical/breakpoints";

const tabletAcima = `(min-width: ${BP_MOBILE + 1}px)`;
const desktop = `(min-width: ${BP_TABLET + 1}px)`;

const tom = (nome, parte) => `var(--${nome || "neutral"}-${parte})`;

export const Pagina = styled(PageContainer)`
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
  padding: 12px 12px 32px;

  @media ${tabletAcima} {
    padding: 16px 20px 40px;
    gap: 16px;
  }
`;

// --------------------------------------------------------------------- topo

export const Topo = styled.div`
  position: sticky;
  top: var(--app-header-height, 0px);
  z-index: 80;
  margin: -12px -12px 0;
  padding: 10px 12px;
  background: ${({ theme }) => theme.surface};
  border-bottom: 1px solid ${({ theme }) => theme.border};
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;

  @media ${tabletAcima} {
    margin: -16px -20px 0;
    padding: 12px 20px;
  }
`;

export const LinhaTitulo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

export const BlocoTitulo = styled.div`
  flex: 1 1 auto;
  min-width: 0;

  h1 {
    margin: 0;
    font-size: 17px;
    font-weight: 700;
    line-height: 1.2;
    color: ${({ theme }) => theme.textPrimary};
  }

  /* No celular o subtítulo não cabe ao lado dos controles: some. */
  p {
    display: none;
    margin: 1px 0 0;
    font-size: var(--text-min, 12px);
    color: ${({ theme }) => theme.textSecondary};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  @media ${tabletAcima} {
    p {
      display: block;
    }
  }

  @media ${desktop} {
    h1 {
      font-size: 20px;
    }
  }
`;

export const Controles = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 6px;
`;

export const Segmentado = styled.div`
  display: inline-flex;
  padding: 2px;
  border-radius: 9px;
  background: ${({ theme }) => theme.surfaceAlt};
  border: 1px solid ${({ theme }) => theme.border};

  button {
    border: 0;
    background: transparent;
    padding: 0 10px;
    min-height: 32px;
    border-radius: 7px;
    font: inherit;
    font-size: 12.5px;
    font-weight: 600;
    color: ${({ theme }) => theme.textSecondary};
    cursor: pointer;
  }

  button[aria-pressed="true"] {
    background: ${({ theme }) => theme.surface};
    color: ${({ theme }) => theme.textPrimary};
    box-shadow: var(--shadow-1, 0 1px 2px rgba(0, 0, 0, 0.08));
  }
`;

export const SeletorOrdem = styled.select`
  display: none;

  @media ${desktop} {
    display: block;
    min-height: 34px;
    padding: 0 8px;
    border-radius: 9px;
    border: 1px solid ${({ theme }) => theme.border};
    background: ${({ theme }) => theme.surface};
    color: ${({ theme }) => theme.textPrimary};
    font: inherit;
    font-size: 12.5px;
  }
`;

export const BotaoFiltros = styled.button`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-width: 36px;
  min-height: 36px;
  padding: 0 10px;
  border-radius: 9px;
  border: 1px solid ${({ theme, $ativo }) => ($ativo ? theme.accent : theme.border)};
  background: ${({ theme }) => theme.surface};
  color: ${({ theme, $ativo }) => ($ativo ? theme.accent : theme.textPrimary)};
  font: inherit;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;

  .rotulo {
    display: none;
  }

  .conta {
    min-width: 17px;
    height: 17px;
    padding: 0 4px;
    border-radius: 999px;
    background: ${({ theme }) => theme.accent};
    color: #fff;
    font-size: 11px;
    font-weight: 700;
    line-height: 17px;
    text-align: center;
    font-variant-numeric: tabular-nums;
  }

  @media ${tabletAcima} {
    .rotulo {
      display: inline;
    }
  }
`;

export const LinhaBusca = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

export const Busca = styled.label`
  position: relative;
  flex: 1 1 auto;
  min-width: 0;

  svg {
    position: absolute;
    left: 10px;
    top: 50%;
    transform: translateY(-50%);
    color: ${({ theme }) => theme.textTertiary};
    pointer-events: none;
  }

  input {
    width: 100%;
    min-height: 34px;
    padding: 6px 10px 6px 30px;
    border-radius: 999px;
    border: 1px solid ${({ theme }) => theme.border};
    background: ${({ theme }) => theme.surfaceAlt};
    color: ${({ theme }) => theme.textPrimary};
    font: inherit;
    /* 16px no celular: abaixo disso o iOS dá zoom ao focar. */
    font-size: 16px;
    outline: none;
  }

  input:focus {
    border-color: ${({ theme }) => theme.accent};
    background: ${({ theme }) => theme.surface};
  }

  @media ${tabletAcima} {
    max-width: 320px;

    input {
      font-size: 13px;
    }
  }
`;

/* Chips dos filtros ativos: uma linha que rola de lado, nunca quebra o topo. */
export const ChipsAtivos = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 5px;
  overflow-x: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

export const ChipAtivo = styled.button`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 220px;
  min-height: 26px;
  padding: 0 8px;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surface};
  color: ${({ theme }) => theme.textPrimary};
  font: inherit;
  font-size: var(--text-min, 12px);
  cursor: pointer;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  svg {
    flex: 0 0 auto;
    color: ${({ theme }) => theme.textTertiary};
  }
`;

export const LinkLimpar = styled.button`
  flex: 0 0 auto;
  border: 0;
  background: none;
  padding: 0 4px;
  min-height: 26px;
  font: inherit;
  font-size: var(--text-min, 12px);
  font-weight: 600;
  color: ${({ theme }) => theme.accent};
  cursor: pointer;
`;

// ------------------------------------------------------------------- resumo

/* Um ponto de cor é o ÚNICO lugar em que o estado ganha cor. */
export const Ponto = styled.i`
  flex: 0 0 auto;
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${({ $tom }) => tom($tom, "text")};
`;

/* Indicadores: uma faixa só, dividida por linhas finas, sem caixas coloridas.
   Número neutro; a cor fica no ponto ao lado do rótulo. */
export const Indicadores = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 12px;
  background: ${({ theme }) => theme.surface};
  overflow: hidden;

  @media ${tabletAcima} {
    grid-template-columns: repeat(6, minmax(0, 1fr));
  }
`;

export const Indicador = styled.button`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 3px;
  min-width: 0;
  padding: 10px 12px;
  border: 0;
  border-right: 1px solid ${({ theme }) => theme.border};
  border-bottom: 1px solid ${({ theme }) => theme.border};
  /* A borda sobra na última coluna/linha; a margem negativa a esconde sob o
     overflow do contêiner em qualquer número de colunas. */
  margin: 0 -1px -1px 0;
  background: ${({ theme, $ativo }) => ($ativo ? theme.surfaceAlt : "transparent")};
  box-shadow: ${({ theme, $ativo }) => ($ativo ? `inset 0 -2px 0 ${theme.accent}` : "none")};
  color: ${({ theme }) => theme.textPrimary};
  font: inherit;
  text-align: left;
  cursor: ${({ as: tag }) => (tag === "div" ? "default" : "pointer")};

  .num {
    font-size: 20px;
    font-weight: 650;
    line-height: 1.1;
    letter-spacing: -0.01em;
    font-variant-numeric: tabular-nums;
    color: ${({ theme, $zerado }) => ($zerado ? theme.textTertiary : theme.textPrimary)};
  }

  .rot {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    max-width: 100%;
    font-size: var(--text-min, 12px);
    color: ${({ theme }) => theme.textSecondary};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* Rótulo que não cabe em 1/3 do celular tem versão curta. */
  .longo {
    display: none;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.accent};
    outline-offset: -2px;
  }

  @media ${tabletAcima} {
    padding: 12px 16px;

    .num {
      font-size: 22px;
    }

    .longo {
      display: inline;
    }

    .curto {
      display: none;
    }
  }
`;

export const LinhaContagem = styled.p`
  margin: 0;
  padding: 0 2px;
  font-size: var(--text-min, 12px);
  color: ${({ theme }) => theme.textSecondary};
  font-variant-numeric: tabular-nums;
`;

export const Aviso = styled.p`
  margin: 0;
  padding: 0 2px;
  color: ${({ theme }) => theme.textSecondary};
  font-size: var(--text-min, 12px);
  line-height: 1.4;

  svg {
    color: var(--warning-text);
    vertical-align: -1px;
  }
`;

// -------------------------------------------------------------------- lista

/* Colunas do desktop: as MESMAS no cabeçalho e em cada linha, para tudo
   alinhar como numa tabela (sem ser uma: não há rolagem lateral em tela
   nenhuma; abaixo do desktop a linha se reorganiza em pilha). */
const colunas = ({ $comValor }) =>
  `minmax(0, 1fr) minmax(0, 1.45fr) 150px 104px 114px 156px ${$comValor ? "112px " : ""}16px`;

export const Bloco = styled.section`
  & + & {
    margin-top: 24px;
  }

  h2 {
    margin: 0 0 8px;
    padding: 0 2px;
    font-size: 13px;
    font-weight: 650;
    color: ${({ theme }) => theme.textPrimary};
  }

  h2 span {
    margin-left: 4px;
    font-weight: 500;
    color: ${({ theme }) => theme.textTertiary};
    font-variant-numeric: tabular-nums;
  }
`;

/* Um contêiner só, linhas separadas por divisória: nada de cartão por linha. */
export const Lista = styled.div`
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 12px;
  background: ${({ theme }) => theme.surface};
  overflow: hidden;
`;

export const LinhaGrupo = styled.div`
  & + & {
    border-top: 1px solid ${({ theme }) => theme.border};
  }
`;

export const Cabecalho = styled.button`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 16px;
  grid-template-areas:
    "quem seta"
    "estado estado"
    "fases fases"
    "datas datas";
  column-gap: 10px;
  row-gap: 8px;
  align-items: center;
  width: 100%;
  min-width: 0;
  padding: 14px;
  border: 0;
  background: transparent;
  color: ${({ theme }) => theme.textPrimary};
  font: inherit;
  text-align: left;
  cursor: pointer;

  .seta {
    grid-area: seta;
    color: ${({ theme }) => theme.textTertiary};
    transition: transform 0.16s cubic-bezier(0.23, 1, 0.32, 1);
  }

  .seta[data-aberto="sim"] {
    transform: rotate(180deg);
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.accent};
    outline-offset: -2px;
  }

  @media (hover: hover) and (pointer: fine) {
    &:not(.colunas):hover {
      background: ${({ theme }) => theme.surfaceAlt};
    }
  }

  /* Cabeçalho de colunas: só existe no desktop. */
  &.colunas {
    display: none;
  }

  @media ${desktop} {
    grid-template-columns: ${colunas};
    grid-template-areas: none;
    column-gap: 16px;
    row-gap: 0;
    padding: 13px 18px;

    .seta {
      grid-area: auto;
    }

    &.colunas {
      display: grid;
      cursor: default;
      padding: 9px 18px;
      border-bottom: 1px solid ${({ theme }) => theme.border};
      font-size: 11px;
      font-weight: 600;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      color: ${({ theme }) => theme.textTertiary};
    }

    &.colunas .numero {
      text-align: right;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .seta {
      transition: none;
    }
  }
`;

export const Quem = styled.div`
  grid-area: quem;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;

  strong {
    font-size: 14px;
    font-weight: 650;
    line-height: 1.3;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  > span {
    display: flex;
    align-items: center;
    font-size: var(--text-min, 12px);
    color: ${({ theme }) => theme.textSecondary};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-variant-numeric: tabular-nums;
  }

  @media ${desktop} {
    grid-area: auto;
  }
`;

export const Fases = styled.div`
  grid-area: fases;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 5px;

  @media ${desktop} {
    grid-area: auto;
  }
`;

/* Barra de avanço: um segmento por fase com ambiente, largura proporcional à
   quantidade. Tons de cinza do claro (escritório) ao escuro (expedição):
   posição no fluxo, não estado; por isso não é cor. Opacidade em vez de
   color-mix: o html2canvas da exportação não entende color-mix. */
export const BarraFases = styled.div`
  display: flex;
  gap: 2px;
  height: 4px;
  max-width: 260px;

  i {
    flex: 1 1 0;
    min-width: 4px;
    border-radius: 2px;
    background: ${({ theme }) => theme.textPrimary};
  }

  i[data-fase="escritorio"] {
    opacity: 0.16;
  }
  i[data-fase="maquinas"] {
    opacity: 0.32;
  }
  i[data-fase="marcenaria"] {
    opacity: 0.5;
  }
  i[data-fase="acabamento"] {
    opacity: 0.7;
  }
  i[data-fase="expedicao"] {
    opacity: 0.92;
  }
`;

export const Legenda = styled.div`
  font-size: var(--text-min, 12px);
  line-height: 1.35;
  color: ${({ theme }) => theme.textSecondary};
  font-variant-numeric: tabular-nums;

  /* Cada par "Fase N" não quebra no meio; a legenda pode usar duas linhas. */
  span {
    white-space: nowrap;
  }

  b {
    font-weight: 600;
    color: ${({ theme }) => theme.textPrimary};
  }
`;

export const Estado = styled.div`
  grid-area: estado;
  min-width: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  column-gap: 8px;
  row-gap: 1px;
  font-variant-numeric: tabular-nums;

  .titulo {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    font-size: 13px;
    font-weight: 550;
    color: ${({ theme }) => theme.textPrimary};
    white-space: nowrap;
  }

  .detalhe {
    font-size: var(--text-min, 12px);
    color: ${({ theme }) => theme.textSecondary};
    white-space: nowrap;
  }

  @media ${desktop} {
    grid-area: auto;
    flex-direction: column;
    flex-wrap: nowrap;
    align-items: flex-start;
  }
`;

/* As três datas (e o valor) ocupam a última linha no celular, cada uma com o
   rótulo na frente; no desktop o invólucro some (display: contents) e cada
   célula cai na sua coluna, com o rótulo no cabeçalho. */
export const Datas = styled.div`
  grid-area: datas;
  min-width: 0;
  display: flex;
  flex-wrap: wrap;
  column-gap: 14px;
  row-gap: 2px;

  @media ${desktop} {
    display: contents;
  }
`;

export const Celula = styled.span`
  min-width: 0;
  font-size: var(--text-min, 12px);
  color: ${({ theme, $vazio }) => ($vazio ? theme.textTertiary : theme.textSecondary)};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;

  &::before {
    content: attr(data-rotulo) " ";
    color: ${({ theme }) => theme.textTertiary};
  }

  @media ${desktop} {
    overflow: hidden;
    text-overflow: ellipsis;
    text-align: ${({ $numero }) => ($numero ? "right" : "left")};

    &::before {
      content: none;
    }
  }
`;

export const Tag = styled.span`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  margin-left: 6px;
  padding: 0 5px;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.border};
  font-size: 11px;
  font-weight: 500;
  line-height: 1.5;
  white-space: nowrap;
  color: ${({ theme, $alerta }) => ($alerta ? "var(--danger-text)" : theme.textSecondary)};
`;

// ------------------------------------------------------ ambientes do grupo

export const Ambientes = styled.ul`
  list-style: none;
  margin: 0;
  padding: 4px 0 8px;
  background: ${({ theme }) => theme.surfaceAlt};
  border-top: 1px solid ${({ theme }) => theme.border};
`;

export const LinhaAmbiente = styled.li`
  button {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    grid-template-areas:
      "nome situacao"
      "etapa prazo";
    column-gap: 10px;
    row-gap: 2px;
    align-items: baseline;
    width: 100%;
    min-width: 0;
    padding: 9px 14px;
    border: 0;
    background: transparent;
    color: ${({ theme }) => theme.textPrimary};
    font: inherit;
    text-align: left;
    cursor: pointer;
  }

  button:focus-visible {
    outline: 2px solid ${({ theme }) => theme.accent};
    outline-offset: -2px;
  }

  @media (hover: hover) and (pointer: fine) {
    button:hover {
      background: ${({ theme }) => theme.surface};
    }
  }

  .nome {
    grid-area: nome;
    min-width: 0;
    font-size: 13px;
    font-weight: 550;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .etapa {
    grid-area: etapa;
    min-width: 0;
    font-size: var(--text-min, 12px);
    color: ${({ theme }) => theme.textSecondary};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .prazo {
    grid-area: prazo;
    font-size: var(--text-min, 12px);
    color: ${({ theme }) => theme.textTertiary};
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
    text-align: right;
  }

  .situacao {
    grid-area: situacao;
    text-align: right;
  }

  @media ${desktop} {
    button {
      grid-template-columns: minmax(0, 1.25fr) minmax(0, 1.6fr) 130px 120px;
      grid-template-areas: none;
      column-gap: 20px;
      padding: 7px 18px 7px 30px;
    }

    .nome,
    .etapa,
    .prazo,
    .situacao {
      grid-area: auto;
    }

    .prazo {
      text-align: left;
    }
  }
`;

/* Situação do ambiente em TEXTO: só atraso e "vence em…" levam cor. */
export const TextoSituacao = styled.span`
  font-size: var(--text-min, 12px);
  font-weight: ${({ $situacao }) => ($situacao === "atrasado" || $situacao === "em_breve" ? 600 : 400)};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  color: ${({ theme, $situacao, $tom }) =>
    $situacao === "atrasado" || $situacao === "em_breve" ? tom($tom, "text") : theme.textTertiary};
`;

export const Vazio = styled.div`
  padding: 36px 16px;
  text-align: center;
  font-size: 13px;
  color: ${({ theme }) => theme.textSecondary};
  border: 1px dashed ${({ theme }) => theme.border};
  border-radius: 12px;

  button {
    margin-top: 10px;
  }
`;

// ------------------------------------------------------- painel de filtros

const surgir = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;
const subir = keyframes`
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
`;
const entrar = keyframes`
  from { transform: translateX(100%); }
  to { transform: translateX(0); }
`;

export const Fundo = styled.div`
  position: fixed;
  inset: 0;
  z-index: 200;
  background: rgba(10, 15, 25, 0.36);
  animation: ${surgir} 0.16s ease;
  display: flex;
  align-items: flex-end;

  @media ${tabletAcima} {
    align-items: stretch;
    justify-content: flex-end;
  }
`;

/* Celular: folha que sobe da base. Tablet/desktop: painel à direita. */
export const Folha = styled.aside`
  width: 100%;
  max-height: 88dvh;
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.surface};
  border-radius: 16px 16px 0 0;
  box-shadow: 0 -12px 32px rgba(0, 0, 0, 0.2);
  animation: ${subir} 0.2s cubic-bezier(0.16, 1, 0.3, 1);
  overflow: hidden;

  @media ${tabletAcima} {
    width: min(400px, 92vw);
    max-height: none;
    height: 100%;
    border-radius: 0;
    border-left: 1px solid ${({ theme }) => theme.border};
    box-shadow: -16px 0 40px rgba(0, 0, 0, 0.2);
    animation-name: ${entrar};
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const CabecaFolha = styled.header`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-bottom: 1px solid ${({ theme }) => theme.border};

  h2 {
    flex: 1 1 auto;
    margin: 0;
    font-size: 15px;
    font-weight: 700;
    color: ${({ theme }) => theme.textPrimary};
  }

  @media ${tabletAcima} {
    padding-top: calc(10px + var(--safe-area-top, 0px));
  }
`;

export const BotaoIcone = styled.button`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 9px;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surface};
  color: ${({ theme }) => theme.textSecondary};
  cursor: pointer;
`;

export const CorpoFolha = styled.div`
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 4px 14px 12px;
  -webkit-overflow-scrolling: touch;
`;

export const Secao = styled.section`
  padding: 10px 0;

  & + & {
    border-top: 1px solid ${({ theme }) => theme.border};
  }

  h3 {
    margin: 0 0 6px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.07em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.textTertiary};
  }
`;

export const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const chipBase = css`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  max-width: 100%;
  min-height: 34px;
  padding: 0 11px;
  border-radius: 999px;
  font: inherit;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  font-variant-numeric: tabular-nums;

  @media ${desktop} {
    min-height: 28px;
    padding: 0 9px;
  }
`;

export const Chip = styled.button`
  ${chipBase}
  border: 1px solid ${({ theme, $ligado }) => ($ligado ? theme.accent : theme.border)};
  background: ${({ theme, $ligado }) => ($ligado ? "var(--accent-soft, rgba(91, 91, 246, 0.12))" : theme.surface)};
  color: ${({ theme, $ligado }) => ($ligado ? theme.accent : theme.textPrimary)};
  /* Fase com só algumas etapas marcadas: borda tracejada. */
  border-style: ${({ $parcial }) => ($parcial ? "dashed" : "solid")};

  span.nome {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  small {
    font-size: 11px;
    font-weight: 500;
    color: ${({ theme, $ligado }) => ($ligado ? theme.accent : theme.textTertiary)};
  }
`;

export const GrupoFase = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  & + & {
    margin-top: 6px;
  }

  .cabeca {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .abrir {
    border: 0;
    background: none;
    padding: 0 6px;
    min-height: 34px;
    font: inherit;
    font-size: var(--text-min, 12px);
    color: ${({ theme }) => theme.textSecondary};
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 3px;
  }

  .etapas {
    padding-left: 10px;
    border-left: 2px solid ${({ theme }) => theme.border};
  }
`;

export const Recolhivel = styled.div`
  .cabeca {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    width: 100%;
    min-height: 36px;
    padding: 0;
    border: 0;
    background: none;
    color: ${({ theme }) => theme.textPrimary};
    font: inherit;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    text-align: left;
  }

  .valor {
    flex: 1 1 auto;
    min-width: 0;
    text-align: right;
    font-weight: 500;
    font-size: var(--text-min, 12px);
    color: ${({ theme }) => theme.textSecondary};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .valor[data-ativo="sim"] {
    color: ${({ theme }) => theme.accent};
    font-weight: 600;
  }

  .corpo {
    padding: 2px 0 8px;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
`;

export const CampoPequeno = styled.input`
  width: 100%;
  min-height: 34px;
  padding: 5px 10px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surface};
  color: ${({ theme }) => theme.textPrimary};
  font: inherit;
  font-size: 16px;

  @media ${tabletAcima} {
    font-size: 13px;
  }
`;

export const LinhaDatas = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  align-items: center;
  gap: 6px;
  font-size: var(--text-min, 12px);
  color: ${({ theme }) => theme.textSecondary};
`;

export const LinhaInterruptor = styled.label`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  min-height: 36px;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.textPrimary};
  cursor: pointer;

  small {
    display: block;
    font-size: var(--text-min, 12px);
    font-weight: 400;
    color: ${({ theme }) => theme.textSecondary};
  }

  input {
    flex: 0 0 auto;
    width: 18px;
    height: 18px;
    accent-color: ${({ theme }) => theme.accent};
  }
`;

export const LinhaSalvar = styled.form`
  display: flex;
  gap: 6px;
  margin-top: 6px;

  button {
    flex: 0 0 auto;
  }
`;

export const VisaoSalva = styled.span`
  display: inline-flex;
  align-items: stretch;
  max-width: 100%;

  ${Chip} {
    border-radius: 999px 0 0 999px;
    border-right: 0;
  }

  .excluir {
    display: inline-flex;
    align-items: center;
    padding: 0 8px;
    border-radius: 0 999px 999px 0;
    border: 1px solid ${({ theme }) => theme.border};
    background: ${({ theme }) => theme.surface};
    color: ${({ theme }) => theme.textTertiary};
    cursor: pointer;
  }
`;

export const RodapeFolha = styled.footer`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px calc(10px + var(--safe-area-bottom, 0px));
  border-top: 1px solid ${({ theme }) => theme.border};

  .principal {
    flex: 1 1 auto;
    justify-content: center;
    min-height: 40px;
  }
`;
